import { remove, send } from './client';
import type { CommandStatus, GatewayState } from './common';
import type { OtaState } from './ota';
import { query, type Cursor, type CursorPage, type OffsetPage } from './paging';
import type { TagRegistryState } from './tags';

/**
 * A gateway heartbeat, stored and answered exactly as the gateway sent it: its keys follow the MQTT contract
 * (rfid-gateway CONTRACT.md), not the API's camelCase.
 */
export interface Heartbeat {
  version?: string; uptime_s?: number; tags_present?: number;
  reader?: { ok: boolean; port: string; error: string | null; errors_total: number; rounds: number; tid_failures: number; info?: { firmware?: string; power_dbm?: number; freq_mhz?: number[] } | null };
  outbox?: { pending: number; last_seq: number; oldest_pending_s: number };
  sys?: { cpu_temp_c: number | null; clock_synced: boolean | null; load1: number };
  update?: UpdateState | null;
}
/** What the gateway's updater last reported (heartbeat `update`, stored as sent). */
export interface UpdateState { state?: string; version?: string; previous?: string; error?: string; at?: string }
/** The backend's side of one gateway's rollout (gateway.ota_request). */
export interface OtaRequest { version: string; rolloutAt: string; sentAt?: string; attempts: number; error?: string }

/**
 * A reader's condition, computed by the server (common/helpers/gateway-condition.helper.ts); the dashboard never
 * derives it. In the order the list sorts them: what needs a look first.
 */
export const GATEWAY_CONDITIONS = ['offline', 'attention', 'never_connected', 'normal'] as const;
export type GatewayCondition = (typeof GATEWAY_CONDITIONS)[number];
/** Why a reader needs a look, most serious first (`OFFLINE` only on an offline one). */
export const GATEWAY_ISSUES = ['OFFLINE', 'NOT_RESPONDING', 'READER_ERROR', 'CLOCK_UNSYNCED', 'OUTBOX_BACKLOG', 'UPDATE_FAILED'] as const;
export type GatewayIssue = (typeof GATEWAY_ISSUES)[number];
/** How GET /gateways may sort: by condition (the default), by name, or the longest silent first. */
export const GATEWAY_SORTS = ['condition', 'name', 'lastSeen'] as const;
export type GatewaySort = (typeof GATEWAY_SORTS)[number];

/** A gateway as GET /gateways lists it. */
export interface Gateway {
  gwId: string;
  site: string;
  label: string | null;
  state: GatewayState;
  stateAt: string | null;
  bootId: string | null;
  version: string | null;
  desiredVersion: string | null;
  updateState: UpdateState | null;
  /** when the backend received `updateState` */
  updateStateAt: string | null;
  otaImmediate: boolean;
  otaRequest: OtaRequest | null;
  /** the update state, computed by the server (the dashboard never derives it) */
  otaState: OtaState;
  lastHeartbeatAt: string | null;
  lastHeartbeat: Heartbeat | null;
  seqOk: number;
  lastBatchAt: string | null;
  locationId: number | null;
  locationName: string | null;
  locationType: string | null;
  tagsPresent: number;
  condition: GatewayCondition;
  /** most serious first; empty when normal */
  issues: GatewayIssue[];
  /** data waiting on the reader to be sent (its last heartbeat); null without a heartbeat */
  outboxPending: number | null;
  locationCode: string | null;
  regencyName: string | null;
  provinceName: string | null;
  /** open alerts about this reader */
  openAlerts: number;
}

/** GET /gateways/summary: how many readers in the caller's scope (and region filter) are in each condition. */
export interface GatewaySummary {
  total: number;
  normal: number;
  attention: number;
  offline: number;
  neverConnected: number;
  unplaced: number;
}

export interface GapRequest {
  epoch: string;
  fromSeq: number;
  toSeq: number;
  attempts: number;
  lastTryAt: string;
  resolved: boolean;
  outcome: 'recovered' | 'lost' | null;
}

/** One read of a reader (GET /gateways/:id/events), newest first; keyed by its event id (a ULID). */
/** One read of a gateway, with its tag's registry state (`usedBy`, `retired`) judged with the TID read with it. */
export interface GatewayEvent extends TagRegistryState {
  id: string;
  type: string;
  ts: string;
  epc: string | null;
  tid: string | null;
  serialNo: string | null;
  cylinderId: number | null;
}

export interface GatewayRecentEvent extends TagRegistryState {
  id: string;
  type: string;
  ts: string;
  epc: string | null;
  tid: string | null;
  /** as the gateway sent it */
  data: Record<string, unknown>;
  serialNo: string | null;
}

/** GET /gateways/:id: the gateway, its latest gap requests and events. */
export interface GatewayDetail extends Gateway {
  currentEpoch: string | null;
  updatedAt: string;
  gapRequests: GapRequest[];
  recentEvents: GatewayRecentEvent[];
}

/** One command sent to a gateway and, once it answered, its result. `args` follow the MQTT contract. */
export interface GatewayCommand {
  id: string;
  gwId: string;
  site: string;
  cmd: string;
  args: Record<string, unknown>;
  status: CommandStatus;
  result: unknown;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * GET /gateways filters. `placed`: at a location (true) or not yet (false); `condition` one or several (comma-separated);
 * `version` the version it runs.
 */
export interface GatewayFilter {
  page?: number;
  limit?: number;
  q?: string;
  state?: GatewayState | '';
  condition?: string;
  provinceCode?: string;
  regencyCode?: string;
  locationId?: number;
  placed?: boolean;
  version?: string;
  sort?: GatewaySort;
}
export type GatewayPage = OffsetPage<Gateway>;
/** Commands are keyed by their (time-ordered) text id. */
export type CommandPage = CursorPage<GatewayCommand, string>;
export type GatewayEventPage = CursorPage<GatewayEvent, string>;
/** A gap request of GET /gateways/:id/gaps: its `id` ("<epoch>:<fromSeq>") is the cursor. */
export interface GapItem extends GapRequest { id: string }
export type GapPage = CursorPage<GapItem, string>;

type Id = number | string | undefined;

/** Alerts that are still open: waiting for someone, or taken and being handled (GET /alerts `state`). */
const OPEN_ALERT_STATES = 'needs_action,handling';

export const gatewayPaths = {
  list: (f: GatewayFilter = {}) => `/gateways${query({ ...f })}`,
  summary: (f: Pick<GatewayFilter, 'provinceCode' | 'regencyCode'> = {}) => `/gateways/summary${query({ ...f })}`,
  detail: (id: Id) => `/gateways/${id}`,
  commands: (id: Id, cursor: Cursor<string>, limit: number) => `/gateways/${id}/commands${query({ ...cursor, limit })}`,
  events: (id: Id, cursor: Cursor<string>, limit: number) => `/gateways/${id}/events${query({ ...cursor, limit })}`,
  gaps: (id: Id, cursor: Cursor<string>, limit: number) => `/gateways/${id}/gaps${query({ ...cursor, limit })}`,
  /** the reader's open alerts, newest first (GET /alerts?gwId=; an AlertPage) */
  openAlerts: (gwId: string, limit: number) => `/alerts${query({ gwId, state: OPEN_ALERT_STATES, limit })}`,
};

/** 200 with the gateway's answer, or 202 with status `sent` while it has not answered yet. */
export const sendGatewayCommand = (gwId: string, cmd: string, args: Record<string, unknown>) => send<GatewayCommand>(`/gateways/${gwId}/commands`, { cmd, args });
export const updateGateway = (gwId: string, body: { label: string | null; locationId: number | null }) => send<Gateway>(`/gateways/${gwId}`, body, 'PATCH');
export const deleteGateway = (gwId: string) => remove(`/gateways/${gwId}`);
